import { createColumnHelper } from "@tanstack/react-table";
import { Loader } from "semantic-ui-react";

import { ReactTable } from "components";
import { USER_STATUSES, User } from "models";
import "modules/Users/components/UsersTable/UsersTable.scss";
import { useGetRoleTranslations, useUsersFilterHandlers, useUsersFilters } from "modules/Users/hooks";
import { useUsersSearch } from "modules/Users/service";

type TableProps = {
  handleOnRowClick: (data: User) => void;
};

export const UsersTable = ({ handleOnRowClick }: TableProps) => {
  const { data, isLoading } = useUsersSearch();
  const rolesTranslations = useGetRoleTranslations();
  const { handlePageSizeChange, handlePaginationChange } = useUsersFilterHandlers();
  const {
    filters: { page: currentPage, page_size: pageSize }
  } = useUsersFilters();

  if (isLoading)
    return (
      <div className="UsersTable__table-loader">
        <Loader active inline />
      </div>
    );

  if (!data) return;

  const { nb_items, nb_pages, users } = data;
  const tablePagination = nb_pages > 0 ? { pageIndex: currentPage - 1, pageSize } : undefined;

  const columnHelper = createColumnHelper<User>();
  const columns = [
    columnHelper.accessor((row, idx) => ({ row, idx }), {
      id: "id",
      header: "#",
      cell: info => {
        const rowIdx = Number(info.getValue().idx) + 1;
        const userNumberOnPage = currentPage > 1 ? (currentPage - 1) * pageSize + rowIdx : rowIdx;
        const userStatus = info.getValue().row.status;

        return (
          <div
            className={`UsersTable__table-user-container ${userStatus === USER_STATUSES.Active ? "UsersTable__table-active-user-container" : "UsersTable__table-inactive-user-container"}`}
          >
            <span className={userStatus === USER_STATUSES.Active ? "UsersTable__table-active-user-text" : "UsersTable__table-inactive-user-text"}>
              {userNumberOnPage}
            </span>
          </div>
        );
      },
      size: 30
    }),
    columnHelper.accessor(row => row.email, {
      id: "email",
      header: "Email",
      cell: info => <span>{info.renderValue()}</span>,
      size: 550
    }),
    columnHelper.accessor(row => row.role, {
      id: "role",
      header: "Role",
      cell: ({ getValue }) => {
        const role = getValue();
        if (!role) return "";
        return <span> {rolesTranslations.find(r => r.role === role)?.translation} </span>;
      },
      size: 50
    }),
    columnHelper.accessor(row => row.status, {
      id: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = getValue();
        if (!status) return "";
        return <span> {status === USER_STATUSES.Active ? "Active" : "Inactive"} </span>;
      },
      size: 50
    })
  ];

  return (
    <div className="UsersTable__table-container">
      <ReactTable
        pageCount={nb_pages}
        pageSize={pageSize}
        totalItems={nb_items}
        pagination={tablePagination}
        onChangePageSize={handlePageSizeChange}
        onPaginationChange={handlePaginationChange}
        columns={columns}
        data={users}
        onRowClick={data => handleOnRowClick(data)}
        renderEmptyRow={!users?.length}
        emptyRowMessage="No data"
      />
    </div>
  );
};
